import {Appointment} from '@/api/MLSChallenge/useGetPlayerAppointments';
import {getLocationBySiteId} from '@/constants/locations';
import {useState} from 'react';

export function formatTime(timestamp: string): string {
  const date = new Date(timestamp.replace('Z', ''));
  if (isNaN(date.getTime())) {
    console.log(`Invalid timestamp: ${timestamp}`);
    return 'Invalid Time';
  }
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

export function formatDate(timestamp: string): string {
  const date = new Date(timestamp.replace('Z', ''));
  if (isNaN(date.getTime())) {
    console.log(`Invalid timestamp: ${timestamp}`);
    return 'Invalid Date';
  }
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

const UpcomingSessionItem = ({appointment}: {appointment: Appointment}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!appointment) {
    return null;
  }
  const {startDateTime, siteId, durationMinutes, sessionTypeName, _id} = appointment;
  const date = formatDate(startDateTime);
  const location = getLocationBySiteId(siteId);
  const time = formatTime(startDateTime);

  return (
    <div
      className="flex items-start gap-6 py-8 border-t border-mls-grey-dark"
      data-test-id={`upcoming-session-item-${_id}`}
    >
      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-mls-primary text-white shadow-sm">
        <span className="text-xs font-semibold" data-test-id="session-time">
          {time}
        </span>
      </div>
      <div className="space-y-3 flex-1 min-w-0">
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            <span data-test-id="session-type">{sessionTypeName || 'Session'} </span>-{' '}
            <span data-test-id="session-date">{date}</span>
          </h3>
          <p className="text-sm text-gray-600">
            <span data-test-id="session-location">{location?.name} </span> •{' '}
            <span data-test-id="session-duration">{durationMinutes} min</span>
          </p>
        </div>
        {
          <div>
            <div
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-2 text-sm text-mls-primary font-medium transition-all duration-200 rounded-lg  px-2 py-1.5 hover:bg-mls-primary hover:bg-opacity-10 border-none"
              data-test-id="session-overview-toggle"
            >
              <span>Session overview</span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {isExpanded && (
              <div className="mt-3 p-4 bg-blue-50 rounded-lg shadow-none">
                <p className="text-sm text-gray-700 leading-relaxed" data-test-id="session-overview-text">
                  This session focuses on passing and receiving fundamentals, and leads into change-of-direction skills
                  that support effective dribbling. As always, we will end the session with some focus on ball striking
                  and finishing.
                </p>
              </div>
            )}
          </div>
        }
      </div>
    </div>
  );
};

const UpcomingSessionBlank = () => {
  return (
    <div className="flex items-center gap-6 py-6 border-t border-mls-grey-dark" data-test-id="upcoming-session-blank">
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-mls-primary text-white">
        <span className="text-2xl font-medium" data-test-id="session-time">
          ?
        </span>
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-medium">
          <span data-test-id="session-type">Oh no! No session booked.</span>
        </h3>
      </div>
    </div>
  );
};

export const UpcomingSessions = ({appointments}: {appointments: Appointment[]}) => {
  // always show exactly 6 session cards, using blanks when missing appointments.
  const sessionsToShow = 6;
  const upcomingSessionsAndPlaceholders = Array(sessionsToShow)
    .fill(null)
    .map((sessionItem, i) => appointments?.[i] ?? null);

  return (
    <div className="rounded-lg bg-mls-grey p-6" data-test-id="upcoming-session-container">
      <h2 className="pb-6 text-lg font-medium">Upcoming</h2>
      {upcomingSessionsAndPlaceholders.map((appointment, i) =>
        appointment ? (
          <UpcomingSessionItem key={appointment._id} appointment={appointment} />
        ) : (
          <UpcomingSessionBlank key={`session_blank_${i}`} />
        )
      )}
    </div>
  );
};
